import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api } from '../api/client.js';
import { ErrorState, Spinner } from '../components/ui.jsx';
import { useFetch, useTitle } from '../lib/hooks.js';
import { formatDate, formatXaf } from '../lib/format.js';
import { saveElementAsPdf } from '../lib/pdf.js';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';

// "Rent for February 2026" reads as "February 2026" in the month column.
const monthText = (charge) => charge.label.replace(/^Rent for\s+/i, '');

const STATUS_TEXT = { paid: 'Paid', partly_paid: 'Part paid', due: 'Unpaid' };

/**
 * The full rent receipt: every rent bill on the account with its paid or unpaid
 * state, the running totals, and the landlord's signature. One document that
 * answers "where does this tenancy stand" - printable, downloadable, and
 * emailable to the tenant.
 */
const PrintableFullReceipt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { platform } = useMeta();
  const toast = useToast();
  const { data, loading, error, reload } = useFetch(`/tenancies/${id}`, [id]);
  const sheetRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const leaseNumber = data ? `NDK-L${String(data.tenancy.id).padStart(5, '0')}` : '';
  useTitle(data ? `Full receipt ${leaseNumber}` : 'Full receipt');

  useEffect(() => {
    document.body.classList.add('printing');
    return () => document.body.classList.remove('printing');
  }, []);

  const downloadPDF = async () => {
    if (!sheetRef.current || !data || saving) return;
    setSaving(true);
    try {
      await saveElementAsPdf(sheetRef.current, {
        margin: 10,
        filename: `full-receipt-${leaseNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      });
    } catch (cause) {
      console.error('Full receipt PDF failed:', cause);
      toast.error('Could not build the PDF. Use the Print button and save as PDF instead.');
    } finally {
      setSaving(false);
    }
  };

  const emailToTenant = async () => {
    if (emailing) return;
    setEmailing(true);
    try {
      const result = await api.post(`/tenancies/${id}/full-receipt/email`);
      toast.success(`Full receipt emailed to ${result.email}.`);
    } catch (cause) {
      toast.error(cause.message);
    } finally {
      setEmailing(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const { tenancy } = data;
  const ledger = tenancy.ledger;

  if (!ledger) {
    return (
      <div className="container section">
        <ErrorState message="This lease has no rent account yet." onRetry={reload} />
      </div>
    );
  }

  const rentCharges = ledger.charges.filter((charge) => charge.kind === 'rent' && charge.status !== 'waived');

  // The latest live payment against each charge gives the paid date column.
  const lastPaid = new Map();
  for (const payment of ledger.payments) {
    if (payment.voided || !payment.chargeId) continue;
    const previous = lastPaid.get(payment.chargeId);
    if (!previous || payment.paidAt > previous) lastPaid.set(payment.chargeId, payment.paidAt);
  }

  const totals = rentCharges.reduce(
    (sum, charge) => ({
      unpaid: sum.unpaid + (charge.outstanding ?? 0),
      paid: sum.paid + (charge.paid ?? 0),
      grand: sum.grand + charge.amount
    }),
    { unpaid: 0, paid: 0, grand: 0 }
  );

  const company = tenancy.landlord?.businessName || tenancy.landlord?.name;
  const signature = tenancy.signatures?.landlord;
  const issuedDate = new Date().toISOString().slice(0, 10);

  return (
    <div className="print-page">
      <div className="print-toolbar">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
          Back
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
          Print
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={downloadPDF} disabled={saving}>
          {saving ? 'Preparing PDF...' : '↓ Download PDF'}
        </button>
        <button type="button" className="btn btn-outline btn-sm" onClick={emailToTenant} disabled={emailing}>
          {emailing ? 'Sending...' : 'Email to tenant'}
        </button>
      </div>

      <article ref={sheetRef} className="lease-sheet full-receipt">
        <header className="lease-doc-head">
          <div>
            {tenancy.landlord?.logoUrl ? <img className="doc-logo" src={tenancy.landlord.logoUrl} alt="" /> : null}
            {company ? <p className="lease-letterhead">{company}</p> : null}
            <h2>Payment receipt</h2>
            <p className="muted">
              Full rent receipt {leaseNumber}
              {platform?.name ? ` - issued through ${platform.name}` : ''} - {formatDate(issuedDate)}
            </p>
          </div>
        </header>

        <dl className="lease-parties">
          <div>
            <dt>Tenant</dt>
            <dd>
              {tenancy.tenant?.name}
              {tenancy.tenant?.phone ? <span className="muted"> {tenancy.tenant.phone}</span> : null}
            </dd>
          </div>
          <div>
            <dt>Property</dt>
            <dd>
              {tenancy.unit?.label ? `${tenancy.unit.label}, ` : ''}
              {tenancy.property?.title}
              {tenancy.property?.address ? <span className="muted"> - {tenancy.property.address}</span> : null}
            </dd>
          </div>
        </dl>

        <table className="full-receipt-table">
          <thead>
            <tr>
              <th>Month</th>
              <th>Due date</th>
              <th className="numeric">Amount</th>
              <th>Status</th>
              <th>Paid date</th>
            </tr>
          </thead>
          <tbody>
            {rentCharges.map((charge) => (
              <tr key={charge.id}>
                <td>{monthText(charge)}</td>
                <td>{charge.status === 'paid' ? '' : formatDate(charge.dueDate)}</td>
                <td className="numeric">{formatXaf(charge.amount)}</td>
                <td>{STATUS_TEXT[charge.status] || charge.status}</td>
                <td>{lastPaid.get(charge.id) ? formatDate(lastPaid.get(charge.id)) : ''}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="total-unpaid">
              <th colSpan={2}>Unpaid total</th>
              <td className="numeric">{formatXaf(totals.unpaid)}</td>
              <td colSpan={2} />
            </tr>
            <tr className="total-paid">
              <th colSpan={2}>Paid total</th>
              <td className="numeric">{formatXaf(totals.paid)}</td>
              <td colSpan={2} />
            </tr>
            <tr className="total-grand">
              <th colSpan={2}>Grand total</th>
              <td className="numeric">{formatXaf(totals.grand)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>

        <footer className="full-receipt-foot">
          <p>
            Issued by: {tenancy.landlord?.name}
            {tenancy.landlord?.phone ? ` (${tenancy.landlord.phone})` : ''}
          </p>
          {signature ? <img className="signature-image" src={signature.url} alt={`Signature of ${signature.name}`} /> : null}
          <span className="receipt-sign-line" />
          <span className="muted">Signature</span>
        </footer>
      </article>
    </div>
  );
};

export default PrintableFullReceipt;
